/**
 * Detecta quando o resultado do PlotCalc excede as dimensões do perfil enviado.
 * Espelha o feedback visual do VBA (contorno laranja > material cinza), sem alterar o apply.
 */

import { micronsToMm } from '../../../../lib/montatech/utils/geometry';
import type {
	MontaTechCurveData,
	MontaTechProfile,
	MaterialResult,
	RFigure,
} from '../types';

/** Margem para ruído numérico (mm). Acima disso conta como estouro real. */
export const MM_BOUNDS_TOLERANCE = 0.1;

const MAX_FIGURE_SAMPLES = 24;

export interface NestingBoundsLineOverflow {
	materialIndex: number;
	lineIndex: number;
	configuredHeightMm: number;
	usedHeightMm: number;
	excessMm: number;
}

export interface NestingBoundsFigureOverflow {
	materialIndex: number;
	lineIndex: number;
	curveIndex: number;
	configuredHeightMm: number;
	positionYmm: number;
	bottomYmm: number;
	excessMm: number;
}

export interface NestingBoundsSheetOverflow {
	materialIndex: number;
	lineIndex: number;
	sheetIndex: number;
	configuredWidthMm: number;
	usedWidthMm: number;
	excessMm: number;
}

export interface NestingBoundsWarning {
	hasIssues: boolean;
	plotcalcErrors: boolean;
	configuredMaterialHeightMm: number;
	configuredMaterialWidthMm: number;
	maximumY_mm: number | null;
	maximumYExcessMm: number | null;
	lineOverflows: NestingBoundsLineOverflow[];
	figureOverflows: NestingBoundsFigureOverflow[];
	sheetOverflows: NestingBoundsSheetOverflow[];
	unplacedCount: number;
	summary: string;
	details: string[];
}

export interface AuditNestingBoundsInput {
	profile: MontaTechProfile;
	materials: MaterialResult[] | null;
	maximumY: number | null;
	plotcalcErrors?: boolean;
	unplacedCount?: number;
	curveData?: MontaTechCurveData | null;
}

function exceedsLimit(valueMm: number, limitMm: number): boolean {
	return valueMm > limitMm + MM_BOUNDS_TOLERANCE;
}

function curveExtentMm(
	curveData: MontaTechCurveData | null | undefined,
	curveIndex: number,
	rotationDeg: number,
): { widthMm: number; heightMm: number } | null {
	if (!curveData) {
		return null;
	}

	const curve = curveData.curves.find((item) => item.index === curveIndex);
	if (!curve) {
		return null;
	}

	let widthMm = curve.width * 25.4;
	let heightMm = curve.height * 25.4;
	const normalizedRotation = ((rotationDeg % 360) + 360) % 360;

	if (normalizedRotation === 90 || normalizedRotation === 270) {
		const swap = widthMm;
		widthMm = heightMm;
		heightMm = swap;
	}

	return { widthMm, heightMm };
}

function iterMaterialFigures(
	materials: MaterialResult[],
): Array<RFigure & { materialIndex: number; lineIndex: number; sheetIndex: number }> {
	const figures: Array<
		RFigure & { materialIndex: number; lineIndex: number; sheetIndex: number }
	> = [];

	for (const material of materials) {
		const materialIndex = material.materialIndex ?? 0;
		material.lines.forEach((line, lineIndex) => {
			line.sheets.forEach((sheet, sheetIndex) => {
				for (const figure of sheet.figures) {
					figures.push({
						...figure,
						materialIndex,
						lineIndex: figure.lineIndex ?? lineIndex,
						sheetIndex: figure.sheetIndex ?? sheetIndex,
					});
				}
			});
		});
	}

	return figures;
}

function buildSummary(parts: string[]): string {
	if (parts.length === 0) {
		return 'O resultado ultrapassou as dimensões do perfil configurado.';
	}
	return parts.join(' ');
}

export function auditNestingBounds(input: AuditNestingBoundsInput): NestingBoundsWarning | null {
	const { profile, materials, maximumY, plotcalcErrors, unplacedCount = 0, curveData } =
		input;

	if (!materials?.length && maximumY == null && !plotcalcErrors && unplacedCount === 0) {
		return null;
	}

	const configuredMaterialHeightMm = micronsToMm(profile.materialHeight);
	const configuredMaterialWidthMm = micronsToMm(profile.materialWith);
	const usableWidthMm =
		configuredMaterialWidthMm -
		micronsToMm(profile.leftField) -
		micronsToMm(profile.rightField);

	const lineOverflows: NestingBoundsLineOverflow[] = [];
	const figureOverflows: NestingBoundsFigureOverflow[] = [];
	const sheetOverflows: NestingBoundsSheetOverflow[] = [];
	const details: string[] = [];
	const summaryParts: string[] = [];

	if (plotcalcErrors) {
		details.push('O motor de encaixe sinalizou erro (algumas peças podem não ter cabido).');
	}

	if (unplacedCount > 0) {
		details.push(`${unplacedCount} peça(s) não foram posicionadas no resultado.`);
		summaryParts.push(`${unplacedCount} peça(s) sem posição.`);
	}

	let maximumYExcessMm: number | null = null;
	if (maximumY != null && exceedsLimit(maximumY, configuredMaterialHeightMm)) {
		maximumYExcessMm = maximumY - configuredMaterialHeightMm;
		details.push(
			`Altura máxima usada (${maximumY.toFixed(1)} mm) excede o perfil (${configuredMaterialHeightMm.toFixed(1)} mm) em ${maximumYExcessMm.toFixed(1)} mm.`,
		);
		summaryParts.push(`altura máx. +${maximumYExcessMm.toFixed(1)} mm`);
	}

	if (materials?.length) {
		for (const material of materials) {
			const materialIndex = material.materialIndex ?? 0;

			material.lines.forEach((line, lineIndex) => {
				if (exceedsLimit(line.height_mm, configuredMaterialHeightMm)) {
					const excessMm = line.height_mm - configuredMaterialHeightMm;
					lineOverflows.push({
						materialIndex,
						lineIndex,
						configuredHeightMm: configuredMaterialHeightMm,
						usedHeightMm: line.height_mm,
						excessMm,
					});
				}

				line.sheets.forEach((sheet, sheetIndex) => {
					if (usableWidthMm > 0 && exceedsLimit(sheet.width_mm, usableWidthMm)) {
						const excessMm = sheet.width_mm - usableWidthMm;
						sheetOverflows.push({
							materialIndex,
							lineIndex,
							sheetIndex,
							configuredWidthMm: usableWidthMm,
							usedWidthMm: sheet.width_mm,
							excessMm,
						});
					}
				});
			});
		}

		for (const figure of iterMaterialFigures(materials)) {
			if (figureOverflows.length >= MAX_FIGURE_SAMPLES) {
				break;
			}

			const positionYmm = figure.localY ?? figure.y;
			const extent = curveExtentMm(curveData, figure.qFigure, figure.rotation);
			const bottomYmm = positionYmm + (extent?.heightMm ?? 0);

			if (
				!exceedsLimit(positionYmm, configuredMaterialHeightMm) &&
				!exceedsLimit(bottomYmm, configuredMaterialHeightMm)
			) {
				continue;
			}

			const excessMm = Math.max(
				positionYmm - configuredMaterialHeightMm,
				bottomYmm - configuredMaterialHeightMm,
			);

			figureOverflows.push({
				materialIndex: figure.materialIndex,
				lineIndex: figure.lineIndex,
				curveIndex: figure.qFigure,
				configuredHeightMm: configuredMaterialHeightMm,
				positionYmm,
				bottomYmm,
				excessMm,
			});
		}
	}

	if (lineOverflows.length > 0) {
		const worst = lineOverflows.reduce((best, item) =>
			item.excessMm > best.excessMm ? item : best,
		);
		details.push(
			`${lineOverflows.length} linha(s) com altura usada acima do perfil (pior: material ${worst.materialIndex + 1}, +${worst.excessMm.toFixed(1)} mm).`,
		);
		summaryParts.push(`${lineOverflows.length} linha(s) acima do limite`);
	}

	if (figureOverflows.length > 0) {
		const sample = figureOverflows
			.slice(0, 5)
			.map((item) => `#${item.curveIndex}`)
			.join(', ');
		const suffix =
			figureOverflows.length > 5 ? ` e mais ${figureOverflows.length - 5}` : '';
		details.push(
			`${figureOverflows.length} peça(s) com coordenada Y acima do perfil (ex.: ${sample}${suffix}).`,
		);
		if (summaryParts.length === 0) {
			summaryParts.push(`${figureOverflows.length} peça(s) acima do limite`);
		}
	}

	if (sheetOverflows.length > 0) {
		const worst = sheetOverflows.reduce((best, item) =>
			item.excessMm > best.excessMm ? item : best,
		);
		details.push(
			`${sheetOverflows.length} chapa(s) com largura acima da área útil (pior: +${worst.excessMm.toFixed(1)} mm).`,
		);
		summaryParts.push(`${sheetOverflows.length} chapa(s) largas demais`);
	}

	const hasIssues =
		plotcalcErrors ||
		unplacedCount > 0 ||
		maximumYExcessMm != null ||
		lineOverflows.length > 0 ||
		figureOverflows.length > 0 ||
		sheetOverflows.length > 0;

	if (!hasIssues) {
		return null;
	}

	return {
		hasIssues: true,
		plotcalcErrors: plotcalcErrors === true,
		configuredMaterialHeightMm,
		configuredMaterialWidthMm,
		maximumY_mm: maximumY,
		maximumYExcessMm,
		lineOverflows,
		figureOverflows,
		sheetOverflows,
		unplacedCount,
		summary: buildSummary(summaryParts),
		details,
	};
}
